import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getTingl, markOpened, openShareLink } from '../services/tingls'
import { getSession } from '../services/auth'
import Detonation, { DetonationData } from '../components/Detonation'

// ─── /receive/:tinglId — from a push notification ───────────────────────────

export function Receive() {
  const { tinglId } = useParams()
  const nav = useNavigate()
  const [data, setData] = useState<DetonationData | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!getSession()) { nav('/'); return }
    if (!tinglId) { nav('/home'); return }
    getTingl(tinglId).then((t) => {
      if (!t) { setFailed(true); return }
      setData({
        vibeKey: t.vibeKey, vibeEmoji: t.vibeEmoji, vibeLabel: t.vibeLabel,
        vibeColor: t.vibeColor, phrase: t.phrase, fromName: t.fromName,
        direction: 'received',
      })
      markOpened(tinglId)
    }).catch(() => setFailed(true))
  }, [tinglId, nav])

  if (failed) {
    return (
      <div className="screen screen-center">
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🫧</div>
          <div style={{ color: 'var(--text-dim)', marginBottom: 24 }}>This tingl has faded away.</div>
          <button className="pill grad" style={{ maxWidth: 240 }} onClick={() => nav('/home')}>Back home</button>
        </div>
      </div>
    )
  }

  if (!data) return <div className="screen screen-center"><div className="logo-dot" style={{ width: 12, height: 12 }} /></div>

  return (
    <Detonation
      data={data}
      onDone={() => nav('/home')}
      onSendBack={() => nav('/home')}
    />
  )
}

// ─── /t/:shortCode — the viral share link, no account needed ────────────────

export function ShareLanding() {
  const { shortCode } = useParams()
  const nav = useNavigate()
  const [data, setData] = useState<DetonationData | null>(null)
  const [failed, setFailed] = useState(false)
  const [tapped, setTapped] = useState(false)

  useEffect(() => {
    if (!shortCode) { setFailed(true); return }
    const attempt = (retriesLeft: number) => {
      openShareLink(shortCode).then((link: any) => {
        if (!link || link.notFound || link.expired || link.isActive === false) { setFailed(true); return }
        apply(link)
      }).catch(() => {
        if (retriesLeft > 0) setTimeout(() => attempt(retriesLeft - 1), 900)
        else setFailed(true)
      })
    }
    // never hang — if nothing arrives in 8s, show the graceful page
    const guard = setTimeout(() => setFailed(true), 8000)
    const apply = (link: any) => {
      clearTimeout(guard)
      setData({
        vibeKey: link.vibeKey, vibeEmoji: link.vibeEmoji,
        vibeLabel: link.vibeKey.replace(/_/g, ' ').toLowerCase(),
        vibeColor: link.vibeColor,
        phrase: link.phrase || 'someone is thinking of you',
        fromName: link.fromName || undefined,
        direction: 'received',
        intensity: link.intensity || 1.0,
      } as any)
    }
    attempt(1)
    return () => clearTimeout(guard)
  }, [shortCode])

  if (failed) {
    return (
      <div className="screen screen-center">
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>👻</div>
          <div style={{ color: 'var(--text-dim)', marginBottom: 24 }}>This feeling has faded — tingls live for 7 days. Someone was thinking of you, though.</div>
          <button className="pill grad" style={{ maxWidth: 260 }} onClick={() => nav('/')}>Send your own tingl →</button>
        </div>
      </div>
    )
  }

  // hold the moment behind one tap — required for sound + haptics to fire
  if (data && !tapped) {
    return (
      <div className="screen screen-center" onClick={() => setTapped(true)} style={{ cursor: 'pointer' }}>
        <div className="fade-in" style={{ textAlign: 'center' }}>
          <div className="logo" style={{ fontSize: 40, justifyContent: 'center' }}>
            <span className="t">t</span><span className="ingl">ingl</span>
            <span className="logo-dot" style={{ width: 7, height: 7, marginBottom: 29, marginLeft: -19 }} />
          </div>
          <div style={{ margin: '30px 0' }}>
            <div className="syne" style={{ fontWeight: 800, fontSize: 26, letterSpacing: '-0.03em' }}>
              Someone sent you<br />a feeling.
            </div>
            <div style={{ color: 'var(--text-dim)', fontSize: 14, marginTop: 10 }}>It lasts one second. Ready?</div>
          </div>
          <button className="pill grad" style={{ maxWidth: 220 }}>Feel it</button>
        </div>
      </div>
    )
  }

  if (!data) return <div className="screen screen-center"><div className="logo-dot" style={{ width: 12, height: 12 }} /></div>

  return (
    <Detonation
      data={data}
      onDone={() => nav('/')}
      onSendBack={() => nav(getSession() ? '/home' : '/signup')}
    />
  )
}
